// src/pages/performance/ManageAppraisalsQuestionDialogs.jsx
import React, { useEffect, useState } from "react";
import api from "../../services/api";


import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from "@mui/material";

import { API, APPLICABILITY_OPTIONS } from "./manageAppraisals.constants";
import { safeMsg } from "./manageAppraisals.utils";

/* ---------------------------------------
   Question Editor
---------------------------------------- */
export function QuestionEditorDialog({ open, initial, onClose, onSaved, onError }) {
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [applicability, setApplicability] = useState("YES");

  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setTitle(initial?.title || "");
    setDescription(initial?.description || "");
    setApplicability(initial?.applicability || "YES");
  }, [open, initial]);

  const handleSave = async () => {
    onError?.("");

    if (!title.trim()) {
      onError?.("Title is required");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${API.questions}/${initial.id}`, {
          title: title.trim(),
          description: description?.trim() || null,
          applicability,
        });
        onSaved?.("Question updated successfully");
      } else {
        await api.post(API.questions, {
          title: title.trim(),
          description: description?.trim() || null,
          applicability,
        });
        onSaved?.("Question created successfully");
      }
      onClose?.();
    } catch (err) {
      onError?.(safeMsg(err, "Failed to save question"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>{isEdit ? "Edit Question" : "Add New Question"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField select label="Applicability" value={applicability} onChange={(e) => setApplicability(e.target.value)} fullWidth>
            {APPLICABILITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------
   Delete Question
---------------------------------------- */
export function DeleteQuestionDialog({ open, row, onClose, onDeleted, onError }) {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSubmitting(false);
  }, [open]);

  const handleDelete = async () => {
    if (!row?.id) return;
    onError?.("");

    setSubmitting(true);
    try {
      await api.delete(`${API.questions}/${row.id}`);
      onDeleted?.("Question deleted successfully");
      onClose?.();
    } catch (err) {
      onError?.(safeMsg(err, "Failed to delete question"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 900 }}>Delete Question</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          This will permanently remove:
          <br />
          <strong>{row?.title || "—"}</strong>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={submitting}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
